/**
 * Data Import Wizard — live-run registry (finding #2).
 *
 * The Import step's run state (phase/progress/summary) stays hook-local in
 * `useRunStepActions` (it survives tab SWITCHES because inactive tabs stay
 * mounted, just CSS-hidden). But a tab CLOSE unmounts that hook and would
 * silently detach a still-writing import. To make the tab-close guard possible
 * WITHOUT hoisting all of the runtime run state, the hook mirrors just the two
 * facts the guard needs — "is a run live for this tab, and under which
 * operationId" — into this registry. `Tabs.tsx` reads it imperatively at
 * close-click time; on confirm it cancels via `operationId` (the existing
 * cancel IPC) before removing the tab.
 */
export type ActiveRunEntry = { operationId: string };
export type ActiveRuns = Record<string, ActiveRunEntry>;

/** Record a run as live for a tab (called at Start, keyed by the run's operationId). */
export function markRunStartedEntries(activeRuns: ActiveRuns, tabId: string, operationId: string): ActiveRuns {
  return { ...activeRuns, [tabId]: { operationId } };
}

/** Drop a tab's live-run marker (called when the run settles: done / cancelled / error, or on unmount). */
export function markRunSettledEntries(activeRuns: ActiveRuns, tabId: string): ActiveRuns {
  if (!(tabId in activeRuns)) return activeRuns; // no-op keeps the reference stable (no needless re-render)
  const next = { ...activeRuns };
  delete next[tabId];
  return next;
}

/** The live-run marker for a tab, or undefined when no import is running there. */
export function activeRunFor(activeRuns: ActiveRuns, tabId: string): ActiveRunEntry | undefined {
  return activeRuns[tabId];
}
